/**
 * The blue banner that tells an agency a Kapitan support session is open in it (TZ §2.3).
 *
 * Shown to every member, because the agency is told. The owner can end the session here;
 * a platform admin who entered the agency from the platform card can leave it here.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type JSX } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { supportApi } from "@/api/platform";
import { queryKeys } from "@/api/queryKeys";
import { refusalText } from "@/features/platform/logic";
import { clearSupportSessionId, readSupportSessionId, supportBannerText } from "@/lib/support";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/ui/button";

const BANNER_REFRESH_MS = 60_000;

export function SupportBanner(): JSX.Element | null {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const banner = useQuery({
    queryKey: queryKeys.support.banner(),
    queryFn: () => supportApi.banner(),
    refetchInterval: BANNER_REFRESH_MS,
    // A support session is open in an agency; a platform admin with no agency has none.
    enabled: Boolean(user?.tenant),
  });

  const end = useMutation({
    mutationFn: () => supportApi.endByOwner(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.support.banner() });
    },
    onError: (error) => {
      toast.error(refusalText(error, "Sessiyani yakunlab bo'lmadi."));
    },
  });

  const session = banner.data?.session ?? null;
  if (banner.data?.active !== true || session === null) {
    return null;
  }

  const isOwner = user?.role === "owner";
  const isEnteredHere = readSupportSessionId() === session.id;

  function leave(): void {
    clearSupportSessionId();
    void navigate("/platforma/tenantlar");
  }

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-3 bg-info px-4 py-2 text-sm text-white md:px-6"
    >
      <span>{supportBannerText(session.reason)}</span>
      {isOwner ? (
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/10 hover:text-white"
          disabled={end.isPending}
          onClick={() => {
            end.mutate();
          }}
        >
          Tugatish
        </Button>
      ) : null}
      {!isOwner && isEnteredHere ? (
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-white/10 hover:text-white"
          onClick={leave}
        >
          Platformaga qaytish
        </Button>
      ) : null}
    </div>
  );
}
